import { observable, action } from "mobx";
import { Orcamento, Configuracao } from "../../../../services";
import { success, error } from "../../../../utils/MensagemUtil";
import * as CsSituacaoOrcamento from "../../../../constantes/CsSituacaoOrcamento";
import * as CsTipoUnidade from "../../../../constantes/CsTipoUnidade";
import * as util from "../../../../utils/MascaraUtil";
import Relatorio from "./Relatorio";
import { validarCamposObrigatorios, isNuloOuVazio } from "../../../../utils/Validate";

class OrcamentoStore {
  @observable
  orcamento = {
    id: null,
    nome: "",
    descrciao: "",
    titulo: "",
    orgao: "",
    percentualTotalBDI: "",
    percentualLeisSociais: "",
    percentualHoristaComDesoneracao: "",
    percentualMensalistaComDesoneracao: "",
    percentualHoristaSemDesoneracao: "",
    percentualMensalistaSemDesoneracao: "",
    etapas: [],
    csSituacaoOrcamento: {}
  };

  @observable
  modal = false;

  @observable
  orcamentos = [];

  @observable
  paginacao = {};

  @observable
  situacoes = CsSituacaoOrcamento.values;

  @observable
  criterio = {
    nome: "",
    situacao: "",
    data: ""
  };

  @observable
  fetching = false;

  @action.bound
  handleChange = ({ target }) => {
    const { name, value } = target;

    this.orcamento[name] = value;
  };

  @action.bound
  handleChangeFiltro = ({ target }) => {
    const { name, value } = target;

    this.criterio[name] = value;
  };

  reset = () => {
    this.orcamento = {
      id: null,
      nome: "",
      descrciao: "",
      titulo: "",
      orgao: "",
      percentualTotalBDI: "",
      percentualLeisSociais: "",
      percentualHoristaComDesoneracao: "",
      percentualMensalistaComDesoneracao: "",
      percentualHoristaSemDesoneracao: "",
      percentualMensalistaSemDesoneracao: "",
      etapas: []
    };
  };

  @action.bound
  async filtrar() {
    try {
      this.fetching = true;

      const result = await Orcamento.consultar(this.criterio);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.orcamentos = [].concat(
        result.data.map(orcamento => ({
          ...orcamento,
          csSituacaoOrcamento: CsSituacaoOrcamento.valueOf(orcamento.situacao),
          isRascunho: CsSituacaoOrcamento.isRascunho(orcamento.situacao)
        }))
      );

      this.paginacao = result.paging;
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async excluir(id) {
    try {
      this.fetching = true;

      const result = await Orcamento.excluir(id);
      if (result.erro) {
        error(result.erro);
        return;
      }

      success("Orcamento excluído com sucesso.");

      this.filtrar();
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  paginar = event => {
    this.criterio = {
      ...this.criterio,
      pagina: event.page,
      limite: event.limit
    };

    this.filtrar();
  };

  @action.bound
  async obter(id) {
    try {
      this.fetching = true;

      const result = await Orcamento.buscar(id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.orcamento = {
        ...result,
        csSituacaoOrcamento: CsSituacaoOrcamento.valueOf(result.situacao)
      };

      await this.calcularTotais();
    } finally {
      this.fetching = false;
    }
  }

  calcularTotais = () => {
    this.orcamento.etapas = this.orcamento.etapas.map(etapa => ({
      ...etapa,
      itens: !isNuloOuVazio(etapa.itens)
        ? etapa.itens.map(item => ({
            ...item,
            servicos: !isNuloOuVazio(item.servicos)
              ? item.servicos.map(servico => {
                  const valorSubTotal = parseFloat(servico.valorTotalMaterial + servico.valorTotalMaoObra + servico.valorTotalLeisSociais);
                  const valorTotalBDI = parseFloat((servico.percentualBdi * valorSubTotal) / 100);

                  return {
                    ...servico,
                    valorSubTotal,
                    valorTotalBDI,
                    valorTotalGeral: valorTotalBDI + valorSubTotal,
                    unidade: CsTipoUnidade.valueOf(servico.tipoUnidade).simbolo,
                    itens: servico.itens
                      ? servico.itens.map(item => ({
                          ...item,
                          valorTotal: item.valor * item.quantidade,
                          unidade: CsTipoUnidade.valueOf(item.tipoUnidade).simbolo
                        }))
                      : []
                  };
                })
              : []
          }))
        : []
    }));
  };

  @action.bound
  async exportar(orcamento) {
    try {
      this.fetching = true;

      const result = await Configuracao.obterEmpresaUsuarioLogado();
      if (result && result.erro) {
        return error(result.erro);
      }

      const empresa = result;

      await this.obterDetalhamento(orcamento.id);

      Relatorio.exportar(empresa, this.orcamento);
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async obterPercentuaisLeisSociais() {
    try {
      this.fetching = true;

      const result = await Orcamento.obterPercentuaisLeisSociais();
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.orcamento = {
        ...this.orcamento,
        percentualLeisSociais: util.percentual(result.percentualHoristaComDesoneracao),
        percentualTotalBDI: util.percentual(result.percentualTotalTaxaBDI),
        percentualHoristaComDesoneracao: util.percentual(result.percentualHoristaComDesoneracao),
        percentualMensalistaComDesoneracao: util.percentual(result.percentualMensalistaComDesoneracao),
        percentualHoristaSemDesoneracao: util.percentual(result.percentualHoristaSemDesoneracao),
        percentualMensalistaSemDesoneracao: util.percentual(result.percentualMensalistaSemDesoneracao)
      };
    } finally {
      this.fetching = false;
    }
  }

  iniciarInclusaoEtapas = history => {
    if (!this.orcamento.id) {
      this.toggle();
    } else {
      history.push(`/cadastros/orcamentos/etapas/${this.orcamento.id}`);
    }
  };

  iniciarInclusaoServicos = history => {
    if (!this.orcamento.id) {
      this.toggle();
    } else {
      history.push(`/cadastros/orcamentos/servicos/${this.orcamento.id}`);
    }
  };

  @action.bound
  async criarNovoOrcamento() {
    try {
      this.fetching = true;

      const erros = this.validarCadastroOrcamento(this.orcamento);
      if (erros.length > 0) {
        error(erros);
        return;
      }

      const result = await Orcamento.criar(this.toOrcamento());
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.orcamento = result;

      success(`Orçamento #${result.id} criado com sucesso!`);
      this.toggle();
    } finally {
      this.fetching = false;
    }
  }

  toOrcamento() {
    const novo = {
      ...this.orcamento,
      percentualLeisSociais: util.remover(this.orcamento.percentualHoristaComDesoneracao),
      percentualTotalBDI: util.remover(this.orcamento.percentualTotalBDI)
    };

    return novo;
  }

  toggle = () => {
    this.modal = !this.modal;
  };

  validarCadastroOrcamento = registro => {
    const erros = validarCamposObrigatorios()
      .addSeNuloOuVazio("Nome", registro.nome)
      .addSeNuloOuVazio("Descrição", registro.descricao)
      .addSeNuloOuVazio("Obra", registro.titulo)
      .addSeNuloOuVazio("Órgão", registro.orgao)
      .addSeNuloOuVazio("Percentuais de Leis Sociais", registro.percentualLeisSociais)
      .addSeNuloOuVazio("Percentual total taxa BDI", registro.percentualTotalBDI)
      .build();

    return erros;
  };

  @action.bound
  async avancarPlanilha(history) {
    try {
      this.fetching = true;

      if (!this.orcamento.id) {
        error("Este orçamento ainda não foi iniciado!\n\nSelecione uma das opções de inclusão para iniciar o orçamento.");
        return;
      }

      if (isNuloOuVazio(this.orcamento.etapas)) {
        error("Você deve incluir ao menos uma etapa para prosseguir!");
        return;
      }

      history.push(`/cadastros/orcamentos/planilha/${this.orcamento.id}`);
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async obterDetalhamento(id) {
    await this.obter(id);
    this.calcularTotaisDetalhe();
  }

  calcularTotaisDetalhe = () => {
    let valorTotalOrcamento = 0;

    if (this.orcamento.etapas) {
      this.orcamento.etapas = this.orcamento.etapas.map(etapa => {
        let valorTotalEtapa = 0;

        return {
          ...etapa,
          itens: etapa.itens.map(item => {
            let valorTotalItem = 0;

            return {
              ...item,
              servicos: item.servicos
                ? item.servicos.map(servico => {
                    const valorSubTotal = servico.valorTotalMaterial + servico.valorTotalMaoObra + servico.valorTotalLeisSociais;
                    const valorTotalBDI = parseFloat((servico.percentualBdi * valorSubTotal).toFixed(2) / 100);
                    const valorTotal = valorSubTotal + valorTotalBDI;
                    const valorTotalGeral = parseFloat((valorTotal * (servico.quantidade || 0)).toFixed(2));

                    valorTotalItem += valorTotalGeral;
                    valorTotalEtapa += valorTotalGeral;
                    valorTotalOrcamento += valorTotalGeral;

                    return {
                      ...servico,
                      valorSubTotal,
                      unidade: CsTipoUnidade.valueOf(servico.tipoUnidade).simbolo,
                      valorTotal: valorTotal,
                      valorTotalGeral: valorTotalGeral,
                      itens: servico.itens.map(item => ({
                        ...item,
                        valorTotal: item.valor * item.quantidade,
                        unidade: CsTipoUnidade.valueOf(item.tipoUnidade).simbolo
                      }))
                    };
                  })
                : null,
              valorTotal: valorTotalItem
            };
          }),
          valorTotal: valorTotalEtapa
        };
      });
    }

    this.orcamento.valorTotalGeral = valorTotalOrcamento;
  };
}

export default new OrcamentoStore();



// WEBPACK FOOTER //
// ./src/views/cadastros/orcamentos/stores/OrcamentoStore.js